/**
 * MaoActionButton - Primary CTA for AI actions
 *
 * A button that triggers AI actions with consistent branding.
 * Shows working state with animation and prevents double-clicks.
 */

import { forwardRef, useCallback, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button, type ButtonProps } from '@/components/ui/button';
import { MaoWorkingIndicator } from './MaoWorkingIndicator';
import { maoLabel, maoLabelShort } from '@/lib/mao';
import type { MaoIntent, MaoSize, MaoAnimationVariant } from '@/lib/mao';

interface MaoActionButtonProps extends Omit<ButtonProps, 'onClick' | 'children'> {
  /** Intent determines the default label */
  intent: MaoIntent;
  /** Custom label (overrides intent-based label) */
  customLabel?: string;
  /** Use short label format */
  shortLabel?: boolean;
  /** Custom noun for the label (e.g., "lista", "textul") */
  customNoun?: string;
  /** Click handler - can be async */
  onClick: () => void | Promise<void>;
  /** Whether the action is currently loading */
  loading?: boolean;
  /** Size variant */
  size?: MaoSize;
  /** Animation variant for loading state */
  animationVariant?: MaoAnimationVariant;
}

export const MaoActionButton = forwardRef<HTMLButtonElement, MaoActionButtonProps>(
  (
    {
      intent,
      customLabel,
      shortLabel = false,
      customNoun,
      onClick,
      loading = false,
      disabled,
      size = 'default',
      animationVariant = 'dots',
      className,
      variant = 'default',
      ...props
    },
    ref,
  ) => {
    const [internalLoading, setInternalLoading] = useState(false);

    // Combine external loading state with internal
    const isLoading = loading || internalLoading;

    // Generate label based on intent
    const label = customLabel
      ? customLabel
      : shortLabel
        ? maoLabelShort(intent)
        : maoLabel(intent, customNoun);

    // Handle click with loading state management
    const handleClick = useCallback(async () => {
      if (isLoading || disabled) return;

      try {
        setInternalLoading(true);
        const result = onClick();
        if (result instanceof Promise) {
          await result;
        }
      } finally {
        setInternalLoading(false);
      }
    }, [onClick, isLoading, disabled]);

    // Map MaoSize to Button size
    const buttonSize = size === 'lg' ? 'lg' : size === 'sm' ? 'sm' : 'default';

    return (
      <Button
        ref={ref}
        variant={variant}
        size={buttonSize}
        onClick={handleClick}
        disabled={disabled || isLoading}
        aria-busy={isLoading}
        className={cn(
          'relative gap-2',
          isLoading && 'cursor-wait',
          className,
        )}
        {...props}
      >
        {isLoading ? (
          <MaoWorkingIndicator
            variant={animationVariant}
            showText={false}
            size={size === 'lg' ? 'default' : 'sm'}
          />
        ) : (
          <Sparkles className={cn(
            size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4',
          )} />
        )}
        <span className={cn(isLoading && 'opacity-70')}>
          {isLoading ? `${label.split(',')[0]} lucrează...` : label}
        </span>
      </Button>
    );
  },
);

MaoActionButton.displayName = 'MaoActionButton';
